import type { ReactNode } from 'react';

import { useRef, useMemo, useState, useEffect, useContext, useCallback, createContext, createElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { fData } from '@nexus/ari-lib/utils/format-number';
import { toast } from '@nexus/ari-lib/components/snackbar';

import { useStore } from '../../lib/store';
import { canUpgradeIn } from '../upgrade/plan-data';
import { cachedCurrentWorkspace } from '../../hooks/use-workspaces';
import { abortUpload, fetchUsage, releaseUploads, releaseUploadsOnLeave } from './documents-api';
import { getSupabaseUrl } from '../../config/global';
import { uploadName } from './documents-data';
import { USAGE_KEY, UNFINISHED_KEY, refreshListing } from './use-documents';
import { UploadError, uploadDocument } from './documents-upload';

import type { UploadPhase } from './documents-upload';
import type { DocumentsUsage, UnfinishedUpload } from './documents-api-types';
import type { Workspace } from '../../hooks/use-workspaces';

export function storageFullText(usage: DocumentsUsage | null, workspace: Workspace | null): string {
  if (!usage) return 'Storage is full. To free space, empty the bin.';
  const head = usage.usedBytes < usage.limitBytes ? 'The file does not fit.' : 'Storage is full.';
  // Only a workspace payer can get more from its billing owner. Until PR #757 a member pays alone.
  let more = 'contact support';
  if (usage.accountKind === 'workspace' && usage.customLimit === false && workspace) {
    if (canUpgradeIn(workspace)) more = 'upgrade your plan';
    else if (!workspace.isBillingOwner) more = 'ask your workspace billing owner';
  }
  return `${head} ${fData(usage.usedBytes)} of ${fData(
    usage.limitBytes
  )} used. To free space, empty the bin or ${more}.`;
}

export type UploadStatus = 'queued' | UploadPhase | 'paused' | 'done' | 'failed';

export type UploadEntry = {
  id: string;
  /** Kept across retries, so the server resumes the same session. */
  clientUploadId: string;
  file: File;
  folderId: string | null;
  replaceDocumentId?: string;
  status: UploadStatus;
  progress: number;
  error: string | null;
  sessionId?: string;
  sessionClosed?: boolean;
  /** Where storage keeps the bytes sent so far, so Retry goes on from there. */
  tusLocation?: string;
  /** Known after the first read of the file, so a resume does not read it again. */
  checksum?: string;
  /** The checksum of the unfinished upload this entry resumes. */
  expectedChecksum?: string;
  /** All bytes may be in storage already, so completion is tried first. */
  completeFirst?: boolean;
  /** The phase the upload failed in. */
  failedWhile?: UploadStatus;
  /** Set by Resume all: the row keeps its percent until storage reports new bytes. CLOUD-1160. */
  resuming?: boolean;
};

/** Queued or on its way: not done, failed or paused. */
export const isActive = (entry: UploadEntry) =>
  entry.status === 'queued' ||
  entry.status === 'checking' ||
  entry.status === 'sending' ||
  entry.status === 'finishing';

/** A place storage gave an earlier attempt. Anything else is not sent to with the upload token. */
function resumableLocation(location: string | null): string | undefined {
  if (!location) return undefined;
  try {
    const url = new URL(location);
    const ours = new URL(getSupabaseUrl()).origin === url.origin;
    return ours && url.pathname.startsWith('/storage/v1/upload/resumable/') ? location : undefined;
  } catch {
    return undefined;
  }
}

/** Why a picked file cannot resume this upload, or null when it can. The checksum is compared while it uploads. */
export function resumeProblem(upload: UnfinishedUpload, file: File): string | null {
  const sameName = upload.replaceDocumentId !== null || uploadName(file.name) === upload.name;
  if (!sameName || file.size !== upload.byteSize) {
    return `This is not the file you started to upload. Pick “${upload.name}” again.`;
  }
  return null;
}

/** `stop` says why the run was stopped, so a session that opens after the stop is paused or cancelled as the user asked. */
type Running = { controller: AbortController; sessionId?: string; stop?: 'pause' | 'cancel' };

/** The share of a paused upload's bytes that storage has. A hash fraction is not sent bytes. */
function pausedProgress(entry: UploadEntry): number {
  if (entry.status === 'finishing') return 1;
  // A resumed upload paused again before new bytes went out keeps what it had sent. CLOUD-1160.
  return entry.status === 'sending' || entry.resuming ? entry.progress : 0;
}

/** One upload at a time left most of each upload waiting on round trips. */
const PARALLEL_UPLOADS = 3;
/** A render per progress report blocked the page during a batch of 1,000 files. */
const DRAW_MS = 150;
const REFRESH_MS = 3000;

function newEntry(file: File, folderId: string | null): UploadEntry {
  return {
    id: crypto.randomUUID(),
    clientUploadId: crypto.randomUUID(),
    file,
    folderId,
    status: 'queued',
    progress: 0,
    error: null,
  };
}

export function useUploadQueue() {
  const queryClient = useQueryClient();
  const [uploads, setUploads] = useState<UploadEntry[]>([]);
  const running = useRef(new Map<string, Running>());
  const cancelled = useRef(new Set<string>());
  const paused = useRef(new Set<string>());
  const waiting = useRef<UploadEntry[]>([]);
  const sending = useRef(0);
  const latest = useRef(uploads);
  latest.current = uploads;

  const changes = useRef(new Map<string, Partial<UploadEntry>>());
  const drawTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draw = useCallback(() => {
    if (drawTimer.current) clearTimeout(drawTimer.current);
    drawTimer.current = null;
    const due = changes.current;
    changes.current = new Map();
    setUploads((prev) => prev.map((entry) => (due.has(entry.id) ? { ...entry, ...due.get(entry.id) } : entry)));
  }, []);
  const patch = useCallback(
    (id: string, change: Partial<UploadEntry>) => {
      changes.current.set(id, { ...changes.current.get(id), ...change });
      drawTimer.current ??= setTimeout(draw, DRAW_MS);
    },
    [draw]
  );

  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshDue = useRef(false);
  const refreshNow = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = null;
    refreshDue.current = false;
    void refreshListing(queryClient);
  }, [queryClient]);
  const refreshSoon = useCallback(() => {
    refreshDue.current = true;
    refreshTimer.current ??= setTimeout(refreshNow, REFRESH_MS);
  }, [refreshNow]);

  useEffect(
    () => () => {
      if (drawTimer.current) clearTimeout(drawTimer.current);
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      drawTimer.current = null;
      refreshTimer.current = null;
    },
    []
  );

  const failureText = useCallback(
    async (error: unknown): Promise<string> => {
      if (!(error instanceof UploadError) || error.code !== 'storage_full') {
        return error instanceof Error ? error.message : 'Upload failed.';
      }
      const usage = await queryClient
        .fetchQuery({ queryKey: USAGE_KEY, queryFn: fetchUsage, staleTime: 0 })
        .catch(() => null);
      const token = useStore.getState().supabaseSession?.access_token;
      return storageFullText(usage, cachedCurrentWorkspace(queryClient, token));
    },
    [queryClient]
  );

  /**
   * Aborts a session and reads the unfinished uploads again, so the panel does not show it. CLOUD-1160.
   * The row goes out of the cache first, so it does not show with Resume while the abort runs.
   */
  const abortSession = useCallback(
    (sessionId: string) => {
      queryClient.setQueryData<UnfinishedUpload[]>(UNFINISHED_KEY, (rows) =>
        rows?.filter((row) => row.sessionId !== sessionId)
      );
      abortUpload(sessionId)
        .catch(() => undefined)
        .finally(() => void queryClient.invalidateQueries({ queryKey: UNFINISHED_KEY }));
    },
    [queryClient]
  );

  const run = useCallback(
    async (entry: UploadEntry) => {
      const controller = new AbortController();
      const state: Running = { controller };
      running.current.set(entry.id, state);
      let phase: UploadStatus = 'queued';

      try {
        const callbacks = {
          signal: controller.signal,
          // A stopped run must not move the row out of Paused.
          onPhase: (status: UploadPhase) => {
            if (controller.signal.aborted) return;
            phase = status;
            patch(entry.id, entry.resuming && status !== 'finishing' ? { status } : { status, progress: 0 });
          },
          onProgress: (progress: number) => {
            if (!controller.signal.aborted) patch(entry.id, { progress });
          },
          onSession: (sessionId: string) => {
            // Stopped while the session opened. Nothing else knows its id. A cancel after a pause also aborts,
            // because the paused run is no longer in `running` and the cancel could not find its session.
            const cancelling = state.stop !== 'pause' || cancelled.current.has(entry.id);
            if (controller.signal.aborted && cancelling) abortSession(sessionId);
            // Paused: the session stays open for Resume all, but gives its room back now.
            else if (controller.signal.aborted && paused.current.has(entry.id)) {
              releaseUploads([sessionId]).catch(() => undefined);
            }
            state.sessionId = sessionId;
            patch(entry.id, { sessionId });
          },
          onLocation: (tusLocation: string) => patch(entry.id, { tusLocation }),
          resumeFrom: entry.tusLocation,
          checksum: entry.checksum,
          onChecksum: (checksum: string) => patch(entry.id, { checksum }),
          expectedChecksum: entry.checksum ? undefined : entry.expectedChecksum,
          completeFirst: entry.completeFirst,
        };
        const stored = await uploadDocument(
          entry.file,
          entry.folderId,
          entry.clientUploadId,
          callbacks,
          entry.replaceDocumentId
        );
        patch(entry.id, { status: 'done', progress: 1 });
        if (stored.outcome === 'unchanged') toast.info('Same file, nothing changed.');
        else if (stored.outcome === 'superseded') {
          toast.info('A newer version of this document is already there. This upload did not replace it.');
        } else if (!entry.replaceDocumentId && stored.name !== uploadName(entry.file.name)) {
          toast.info(`Saved as “${stored.name}”.`);
        }
        refreshSoon();
      } catch (error) {
        if (controller.signal.aborted) return;
        // Another file picked to resume an unfinished upload. The upload stays in the panel to pick again.
        if (error instanceof UploadError && error.code === 'other_file') {
          toast.error(`${error.message} Pick the right file again in Unfinished uploads.`);
          setUploads((prev) => prev.filter((one) => one.id !== entry.id));
          return;
        }
        const sessionClosed = error instanceof UploadError && error.code === 'closed';
        // A failed upload stays open for Retry, but gives its room back now. CLOUD-1167.
        if (state.sessionId && !sessionClosed) releaseUploads([state.sessionId]).catch(() => undefined);
        const text = await failureText(error);
        // Paused or cancelled while the message was read. The row is Paused or gone, so it must not show Failed.
        if (controller.signal.aborted) return;
        patch(entry.id, { status: 'failed', error: text, sessionClosed, failedWhile: phase });
      } finally {
        // After Pause all and Resume all, a new run of the same entry can be in the map already.
        if (running.current.get(entry.id) === state) running.current.delete(entry.id);
      }
    },
    [patch, failureText, refreshSoon, abortSession]
  );

  const enqueue = useCallback(
    (entry: UploadEntry) => {
      waiting.current.push(entry);
      const next = () => {
        while (sending.current < PARALLEL_UPLOADS && waiting.current.length > 0) {
          const queued = waiting.current.shift() as UploadEntry;
          if (cancelled.current.has(queued.id) || paused.current.has(queued.id)) continue;
          sending.current += 1;
          void run(queued).finally(() => {
            sending.current -= 1;
            next();
            if (sending.current === 0 && refreshDue.current) refreshNow();
          });
        }
      };
      next();
    },
    [run, refreshNow]
  );

  const add = useCallback(
    (entries: UploadEntry[]) => {
      setUploads((prev) => [...prev, ...entries]);
      entries.forEach(enqueue);
    },
    [enqueue]
  );

  const start = useCallback(
    (files: File[], folderId: string | null) => add(files.map((file) => newEntry(file, folderId))),
    [add]
  );

  const replace = useCallback(
    (file: File, document: { id: string; folderId: string | null }) =>
      add([{ ...newEntry(file, document.folderId), replaceDocumentId: document.id }]),
    [add]
  );

  // A failed or retried upload keeps its session id on the entry, and the session holds room until it is aborted.
  const cancel = useCallback(
    (id: string) => {
      const shown = latest.current.find((one) => one.id === id);
      const entry = shown && { ...shown, ...changes.current.get(id) };
      if (entry?.status === 'done') return;
      cancelled.current.add(id);
      const state = running.current.get(id);
      if (state) state.stop = 'cancel';
      state?.controller.abort();
      running.current.delete(id);
      const sessionId = state?.sessionId ?? entry?.sessionId;
      if (sessionId && !entry?.sessionClosed) abortSession(sessionId);
      setUploads((prev) => prev.filter((one) => one.id !== id));
    },
    [abortSession]
  );

  const retry = useCallback(
    (id: string) => {
      const shown = latest.current.find((one) => one.id === id);
      if (!shown) return;
      const entry = { ...shown, ...changes.current.get(id) };
      // A stale pause mark makes the queue skip the entry, and the row stays Queued.
      paused.current.delete(id);
      const fresh: UploadEntry = {
        ...entry,
        clientUploadId: entry.sessionClosed ? crypto.randomUUID() : entry.clientUploadId,
        tusLocation: entry.sessionClosed ? undefined : entry.tusLocation,
        // A completion that failed may have failed only on the way back. CLOUD-1107.
        completeFirst: !entry.sessionClosed && entry.failedWhile === 'finishing',
        status: 'queued',
        progress: 0,
        error: null,
        sessionClosed: false,
        resuming: false,
      };
      patch(id, fresh);
      draw();
      enqueue(fresh);
    },
    [patch, draw, enqueue]
  );

  /** Stops every running and waiting upload. Each gives its room back and can go on later. CLOUD-1160. */
  const pauseAll = useCallback(() => {
    const now = latest.current.map((one) => ({ ...one, ...changes.current.get(one.id) }));
    const sessions: string[] = [];
    for (const entry of now.filter(isActive)) {
      paused.current.add(entry.id);
      const state = running.current.get(entry.id);
      if (state) state.stop = 'pause';
      state?.controller.abort();
      running.current.delete(entry.id);
      const sessionId = state?.sessionId ?? entry.sessionId;
      if (sessionId && !entry.sessionClosed) sessions.push(sessionId);
      patch(entry.id, { status: 'paused', progress: pausedProgress(entry), ...(sessionId ? { sessionId } : {}) });
    }
    waiting.current = waiting.current.filter((queued) => !paused.current.has(queued.id));
    draw();
    if (sessions.length) releaseUploads(sessions.slice(0, 50)).catch(() => undefined);
  }, [patch, draw]);

  const resumeAll = useCallback(() => {
    const now = latest.current.map((one) => ({ ...one, ...changes.current.get(one.id) }));
    for (const entry of now.filter((one) => one.status === 'paused')) {
      paused.current.delete(entry.id);
      // A file paused before any byte went out is read and checked again as new.
      const fresh: UploadEntry = { ...entry, status: 'queued', error: null, resuming: entry.progress > 0 };
      if (!fresh.resuming) fresh.progress = 0;
      patch(entry.id, fresh);
      enqueue(fresh);
    }
    draw();
  }, [patch, draw, enqueue]);

  /** Cancels every upload that is not done. Each frees its room at once. CLOUD-1160. */
  const cancelAll = useCallback(() => {
    const now = latest.current.map((one) => ({ ...one, ...changes.current.get(one.id) }));
    for (const entry of now.filter((one) => one.status !== 'done')) {
      cancelled.current.add(entry.id);
      const state = running.current.get(entry.id);
      if (state) state.stop = 'cancel';
      state?.controller.abort();
      running.current.delete(entry.id);
      const sessionId = state?.sessionId ?? entry.sessionId;
      if (sessionId && !entry.sessionClosed) abortSession(sessionId);
    }
    setUploads((prev) => prev.filter((one) => one.status === 'done'));
  }, [abortSession]);

  /** Goes on with an upload a closed tab or a crash left unfinished, with the file picked again. CLOUD-1167. */
  const resume = useCallback(
    (unfinished: UnfinishedUpload, file: File) => {
      add([
        {
          ...newEntry(file, unfinished.folderId),
          clientUploadId: unfinished.clientUploadId,
          sessionId: unfinished.sessionId,
          replaceDocumentId: unfinished.replaceDocumentId ?? undefined,
          tusLocation: resumableLocation(unfinished.resumeLocation),
          expectedChecksum: unfinished.checksumSha256,
          completeFirst: unfinished.storedBytes === unfinished.byteSize,
        },
      ]);
    },
    [add]
  );

  const clearFinished = useCallback(() => {
    latest.current
      .filter((entry) => entry.status === 'failed' && entry.sessionId && !entry.sessionClosed)
      .forEach((entry) => abortSession(entry.sessionId as string));
    setUploads((prev) => prev.filter((entry) => entry.status !== 'done' && entry.status !== 'failed'));
  }, [abortSession]);

  // A reload or a closed tab ends every upload. What was running gives its room back at once.
  const busy = uploads.some(isActive);
  useEffect(() => {
    if (!busy) return undefined;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    const leave = () => {
      const sessions = [...running.current.values()].flatMap((state) => (state.sessionId ? [state.sessionId] : []));
      releaseUploadsOnLeave(sessions, useStore.getState().supabaseSession?.access_token);
    };
    window.addEventListener('beforeunload', warn);
    window.addEventListener('pagehide', leave);
    return () => {
      window.removeEventListener('beforeunload', warn);
      window.removeEventListener('pagehide', leave);
    };
  }, [busy]);

  return { uploads, start, replace, cancel, retry, clearFinished, pauseAll, resumeAll, cancelAll, resume };
}

export type DocumentsUploads = Omit<ReturnType<typeof useUploadQueue>, 'uploads'>;

/** Apart from the tray, the page reads only the actions, which never change. */
const DocumentsUploadsContext = createContext<DocumentsUploads | null>(null);
const UploadListContext = createContext<UploadEntry[]>([]);

/** Above the routes, so uploads go on while the user is on another page. */
export function DocumentsUploadsProvider({ children }: { children: ReactNode }) {
  const { uploads, start, replace, cancel, retry, clearFinished, pauseAll, resumeAll, cancelAll, resume } =
    useUploadQueue();
  const actions = useMemo(
    () => ({ start, replace, cancel, retry, clearFinished, pauseAll, resumeAll, cancelAll, resume }),
    [start, replace, cancel, retry, clearFinished, pauseAll, resumeAll, cancelAll, resume]
  );
  return createElement(
    DocumentsUploadsContext.Provider,
    { value: actions },
    createElement(UploadListContext.Provider, { value: uploads }, children)
  );
}

export function useDocumentsUploads(): DocumentsUploads | null {
  return useContext(DocumentsUploadsContext);
}

export function useUploadList(): UploadEntry[] {
  return useContext(UploadListContext);
}
